// Shared period chips + filter drawer for /insights and all its sub-pages.
// Extracted from Insights.tsx so drill-in pages can switch period without
// going back to the hub. Reads + writes the same `?from&to&user_id` URL
// search params via useInsightsRange + useSearchParams. Applying a range
// preserves other exploration filters and clears any now-stale day selection.
// The compact landing variant moves presets into the same drawer; report pages
// retain their inline shortcuts and the same URL/filter semantics.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { useInsightsRange } from '@/hooks/useInsightsRange';
import { AppDrawer } from '@/components/ui/AppDrawer';
import type { MemberRow } from '@/lib/insightsTypes';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function fmt(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function presets(t: (key: string) => string, fyStartMonth: number) {
  const today = new Date();
  const startThisMonth = startOfMonth(today);
  const endThisMonth = endOfMonth(today);
  const startLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
  const start3Months = new Date(today.getFullYear(), today.getMonth() - 2, 1);

  const fyStartIdx = fyStartMonth - 1;
  const fyStartYear =
    today.getMonth() >= fyStartIdx ? today.getFullYear() : today.getFullYear() - 1;
  const startFY = new Date(fyStartYear, fyStartIdx, 1);
  const endFY = new Date(fyStartYear + 1, fyStartIdx, 0);

  return [
    { id: 'this_month', label: t('insights.rangeMonth'), from: fmt(startThisMonth), to: fmt(endThisMonth) },
    { id: 'last_3', label: t('insights.rangeQuarter'), from: fmt(start3Months), to: fmt(endThisMonth) },
    { id: 'fy_to_date', label: t('insights.rangeFyToDate'), from: fmt(startFY), to: fmt(today) },
    { id: 'fy', label: t('insights.rangeFy'), from: fmt(startFY), to: fmt(endFY) },
    { id: 'last_month', label: t('insights.rangeLastMonth'), from: fmt(startLastMonth), to: fmt(endLastMonth) },
  ];
}

export function InsightsPeriodPicker({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const [params, setParams] = useSearchParams();
  const { fromParam, toParam, userIdParam } = useInsightsRange();

  const [fromInput, setFromInput] = useState(fromParam);
  const [toInput, setToInput] = useState(toParam);
  const [userInput, setUserInput] = useState(userIdParam);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setFromInput(fromParam);
    setToInput(toParam);
    setUserInput(userIdParam);
  }, [fromParam, toParam, userIdParam]);

  function applyRange(from: string, to: string, userId: string) {
    const next = new URLSearchParams(params);
    next.set('from', from);
    next.set('to', to);
    if (userId) next.set('user_id', userId);
    else next.delete('user_id');
    next.delete('day');
    next.delete('receipt');
    setParams(next);
  }

  const { data: members } = useQuery({
    queryKey: ['members', ws?.id],
    queryFn: () => api.get<MemberRow[]>(`/workspaces/${ws!.id}/members`),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const { data: profile } = useQuery({
    queryKey: ['advisor', 'household-profile', ws?.id],
    queryFn: () => api.get<{ fy_start_month: number }>('/advisor/household-profile'),
    enabled: !!ws,
    staleTime: 60 * 1000,
  });
  const fyStartMonth = profile?.fy_start_month ?? 7;
  const zh = i18n.language.startsWith('zh');
  const selectedMember = members?.find((member) => member.id === userIdParam);
  const memberLabel = userIdParam
    ? selectedMember?.name?.trim() || selectedMember?.email || userIdParam
    : t('insights.allMembers');
  const fromDate = new Date(`${fromParam}T12:00:00`);
  const fullMonth = fromParam.endsWith('-01') && !Number.isNaN(fromDate.getTime()) && toParam === fmt(endOfMonth(fromDate));
  const periodLabel = fullMonth
    ? new Intl.DateTimeFormat(zh ? 'zh-CN' : 'en-AU', { year: 'numeric', month: 'long' }).format(fromDate)
    : `${fromParam} → ${toParam}`;

  return (
    <>
      {compact ? <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setShowFilters(true)} aria-label={zh ? `选择日期范围：${periodLabel}` : `Choose period: ${periodLabel}`} aria-haspopup="dialog" aria-expanded={showFilters} className="flex min-h-11 min-w-0 flex-[1.3] items-center gap-2 rounded-xl border bg-card px-3 py-2 text-left text-xs hover:bg-muted">
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 break-words leading-relaxed">{periodLabel}</span><ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
        <button type="button" onClick={() => setShowFilters(true)} aria-label={zh ? `选择成员：${memberLabel}` : `Choose member: ${memberLabel}`} aria-haspopup="dialog" aria-expanded={showFilters} className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border bg-card px-3 py-2 text-left text-xs hover:bg-muted">
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /><span className="min-w-0 flex-1 break-all leading-relaxed">{memberLabel}</span><ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </div> : <div className="rounded-2xl border bg-card p-3">
        <div className="mb-1.5 text-[11px] text-muted-foreground">
          {fromParam} → {toParam}
          {userIdParam && members && (
            <>
              {' · '}
              {members.find((m) => m.id === userIdParam)?.name ||
                members.find((m) => m.id === userIdParam)?.email ||
                userIdParam}
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {presets(t, fyStartMonth).map((p) => {
            const active = p.from === fromParam && p.to === toParam;
            return (
              <button
                key={p.id}
                onClick={() => applyRange(p.from, p.to, userIdParam)}
                className={`inline-flex min-h-[44px] items-center rounded-full px-3 text-xs font-medium ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'border bg-background hover:bg-muted'
                }`}
              >
                {p.label}
              </button>
            );
          })}
          <button
            onClick={() => setShowFilters(true)}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-full border bg-background px-3 text-xs font-medium hover:bg-muted"
            aria-label={t('insights.more')}
          >
            <span aria-hidden>⚙</span>
            {t('insights.more')}
          </button>
        </div>
      </div>}

      <AppDrawer
        open={showFilters}
        onOpenChange={setShowFilters}
        title={t('insights.filtersTitle')}
      >
        <div className="space-y-5 pb-2">
          {compact && <section aria-label={zh ? '快捷日期' : 'Quick periods'}>
            <h3 className="mb-2 text-xs font-medium text-muted-foreground">{zh ? '快捷日期' : 'Quick periods'}</h3>
            <div className="flex flex-wrap gap-2">{presets(t, fyStartMonth).map((preset) => <button key={preset.id} type="button" aria-pressed={preset.from === fromParam && preset.to === toParam} onClick={() => { applyRange(preset.from, preset.to, userIdParam); setShowFilters(false); }} className={`min-h-11 rounded-xl px-3 text-xs font-medium ${preset.from === fromParam && preset.to === toParam ? 'bg-primary-soft-bg text-primary-soft-fg' : 'border bg-background hover:bg-muted'}`}>{preset.label}</button>)}</div>
          </section>}
          <section>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t('insights.customRangeLabel')}
            </div>
            <div className="flex flex-col gap-2">
              <input
                type="date"
                aria-label={zh ? '开始日期' : 'Start date'}
                value={fromInput}
                onChange={(e) => setFromInput(e.target.value)}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm"
              />
              <input
                type="date"
                aria-label={zh ? '结束日期' : 'End date'}
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm"
              />
              <button
                onClick={() => {
                  applyRange(fromInput, toInput, userInput);
                  setShowFilters(false);
                }}
                disabled={!fromInput || !toInput || fromInput > toInput}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {t('insights.applyRange')}
              </button>
            </div>
          </section>
          <section>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t('insights.workspace.spender', { defaultValue: i18n.language.startsWith('zh') ? '消费归属人' : 'Spender' })}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => {
                  applyRange(fromParam, toParam, '');
                  setShowFilters(false);
                }}
                className={`inline-flex min-h-[44px] items-center rounded-full px-3 text-xs font-medium ${
                  userIdParam === ''
                    ? 'bg-primary text-primary-foreground'
                    : 'border bg-background hover:bg-muted'
                }`}
              >
                {t('insights.allMembers')}
              </button>
              {members?.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    applyRange(fromParam, toParam, m.id);
                    setShowFilters(false);
                  }}
                  className={`inline-flex min-h-[44px] items-center rounded-full px-3 text-xs font-medium ${
                    userIdParam === m.id
                      ? 'bg-primary text-primary-foreground'
                      : 'border bg-background hover:bg-muted'
                  }`}
                >
                  {m.name?.trim() || m.email}
                </button>
              ))}
            </div>
          </section>
        </div>
      </AppDrawer>
    </>
  );
}
